import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { StatNumber } from '../ui/StatsLoading';
import { cn } from '../../lib/utils';

export type StatTone = 'default' | 'accent' | 'pos' | 'warn' | 'danger' | 'info' | 'muted';

const toneClass: Record<StatTone, string> = {
  default: 'text-glass-text',
  accent: 'text-accent2',
  pos: 'text-[rgb(var(--pos))]',
  warn: 'text-[rgb(var(--warn))]',
  danger: 'text-[rgb(var(--danger))]',
  info: 'text-[rgb(var(--info))]',
  muted: 'text-glass-text-3',
};

interface StatTileProps {
  label: ReactNode;
  value: ReactNode;
  /** Secondary line under the value (e.g. "12 up · 3 down"). */
  sub?: ReactNode;
  tone?: StatTone;
  loading?: boolean;
  /** Renders the tile as an internal link. */
  to?: string;
  onClick?: () => void;
  /** Tooltip on the whole tile. */
  title?: string;
  className?: string;
}

export default function StatTile({
  label,
  value,
  sub,
  tone = 'default',
  loading,
  to,
  onClick,
  title,
  className,
}: StatTileProps) {
  const interactive = Boolean(to || onClick);
  const body = (
    <>
      <div className="text-sm text-glass-text-2">{label}</div>
      <div className={cn('stat-display mt-1 text-2xl', toneClass[tone])}>
        <StatNumber loading={loading} spinnerClassName="h-6 w-6 animate-spin text-glass-text-3">
          {value}
        </StatNumber>
      </div>
      {sub != null && <div className="mt-1 text-xs text-glass-text-3">{sub}</div>}
    </>
  );

  const classes = cn(
    'glass-card block p-5 text-left transition-colors',
    interactive && 'cursor-pointer hover:bg-glass-3/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50',
    className
  );

  if (to) {
    return (
      <Link to={to} className={classes} title={title}>
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cn(classes, 'w-full')} title={title}>
        {body}
      </button>
    );
  }
  return (
    <div className={classes} title={title}>
      {body}
    </div>
  );
}
